import { Loader } from '@googlemaps/loader'
import { get } from 'svelte/store'
import { addMessages, init, getLocaleFromPathname } from 'svelte-i18n'
import { mapLocations } from '../stores'
import en from '../lang/en.json'
import fr from '../lang/fr.json'

class DistrictMap {
    constructor() {
        this.pos = {
            lat: 0,
            lng: 0,
        }

        this.map = ''

        this.windowLocations = window.locations

        this.defaultLang = window.mapOptions.lang

        this.boot()
    }

    async boot() {
        addMessages('en', en)
        addMessages('fr', fr)

        init({
            fallbackLocale: this.defaultLang,
            initialLocale: getLocaleFromPathname(/^\/(.*?)\//),
        })

        try {
            await this.setPos().then(() => {
                this.initializeMap()
                this.sortLocations()
            })
        } catch (e) {
            this.initializeMap()
            this.sortLocations()
            console.log(e)
        }

        this.searchListener()
    }

    async setPos() {
        if (!navigator.geolocation)
            throw new Error('This browser does not support Geolocation')

        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    }

                    resolve()
                },
                ({ code, message }) => {
                    this.pos = {
                        lat: window.mapOptions.lat,
                        lng: window.mapOptions.lng,
                    }
                    reject({ code, message })
                },
            )
        })
    }

    initializeMap() {
        const mapEl = document.getElementById('map')

        const loader = new Loader({
            apiKey: window.mapOptions.api_key,
            version: 'weekly',
        })

        loader
            .load()
            .then(() => {
                if (mapEl) {
                    const mapOptions = {
                        center: new google.maps.LatLng(
                            this.pos.lat,
                            this.pos.lng,
                        ),
                        zoom: 6,
                        disableDefaultUI: true,
                    }

                    this.map = new google.maps.Map(mapEl, mapOptions)
                    const infoWindow = new google.maps.InfoWindow()

                    for (const location of window.locations) {
                        let content = ``

                        if (location.image)
                            content += `<img class="map-infowindow-img" src="${location.image}" alt="${location.title}"/>`

                        content += `<h5 class="map-infowindow-title">${location.title}</h5>`

                        if (location.content)
                            content += `
                        <div class="map-infowindow-content">
                            ${location.content}
                        </div>
                        `

                        if (location.general.address)
                            content += `
                        <div class="map-infowindow-address">
                            ${location.general.address}
                        </div>
                        `

                        if (location.general.phone)
                            content += `
                        <div class="map-infowindow-phone">
                            <a href="tel:${location.general.phone}">${location.general.phone}</a>
                        </div>
                        `

                        if (location.social)
                            content += `<div class="map-infowindow-social">`
                        for (const social of location.social) {
                            content += `<a href="${social.social_url}">${social.social_type}</a>`
                        }
                        content += `</div>`

                        const marker = new google.maps.Marker({
                            position: new google.maps.LatLng(
                                location.coordinates.lat,
                                location.coordinates.lng,
                            ),
                            map: this.map,
                        })

                        google.maps.event.addListener(marker, 'click', () => {
                            infoWindow.setContent(content)
                            infoWindow.open(this.map, marker)
                        })
                    }
                }
            })
            .catch((e) => {
                throw new Error(e)
            })

        this.findClosestLocation(window.locations, this.pos)
    }

    distance(lat, lng, userLat, userlng) {
        const radLat = (Math.PI * lat) / 180
        const radUserLat = (Math.PI * userLat) / 180
        const theta = lng - userlng
        const radTheta = (Math.PI * theta) / 180

        let dist =
            Math.sin(radLat) * Math.sin(radUserLat) +
            Math.cos(radLat) * Math.cos(radUserLat) * Math.cos(radTheta)

        dist = Math.acos(dist)
        dist = (dist * 180) / Math.PI
        dist = dist * 60 * 1.1515
        dist = dist * 1.609344

        return dist
    }

    async findClosestLocation(locations, pos) {
        return new Promise(
            (resolve, reject) => {
                locations.forEach((location) => {
                    location.distance = this.distance(
                        pos.lat,
                        pos.lng,
                        location.coordinates.lat,
                        location.coordinates.lng,
                    )
                })

                resolve(locations)
            },
            ({ code, message }) => {
                reject({ code, message })
            },
        )
    }

    async sortLocations() {
        await this.findClosestLocation(this.windowLocations, this.pos).then(
            (locations) => {
                mapLocations.set(
                    [...locations].sort((a, b) => a.distance - b.distance),
                )
            },
        )

        return mapLocations
    }

    searchListener() {
        const searchSubmit = document.getElementById('map-search-submit')
        const searchReset = document.getElementById('map-search-reset')
        const searchLocate = document.getElementById('map-search-locate')
        const searchInput = document.getElementById('map-search-input')

        if (searchSubmit) {
            searchSubmit.addEventListener('click', () => {
                this.fetchCoordinates(searchInput.value).then((data) => {
                    const location = data.results[0].geometry.location

                    this.findClosestLocation(get(mapLocations), location).then(
                        (locations) => {
                            mapLocations.set(
                                [...locations].sort(
                                    (a, b) => a.distance - b.distance,
                                ),
                            )
                        },
                    )

                    this.centerMap(`${location.lat},${location.lng}`, 12)
                })

                if (searchReset) searchReset.style.display = 'block'
            })
        }

        if (searchLocate) {
            searchLocate.addEventListener('click', () => {
                this.findClosestLocation(get(mapLocations), location)
                    .then((locations) => {
                        mapLocations.set(
                            [...locations].sort(
                                (a, b) => a.distance - b.distance,
                            ),
                        )
                    })
                    .then(() => {
                        const location = get(mapLocations)
                        this.centerMap(
                            `${location[0].coordinates.lat},${location[0].coordinates.lng}`,
                            12,
                        )
                    })
            })
        }

        this.searchReset()
    }

    centerMap(address, zoomLevel = 13) {
        this.fetchCoordinates(address).then((data) => {
            this.map.setCenter(data.results[0].geometry.location)
            this.map.setZoom(zoomLevel)
        })
    }

    searchReset() {
        const searchInput = document.getElementById('map-search-input')
        const resetInput = document.getElementById('map-search-reset')

        if (resetInput) {
            resetInput.addEventListener('click', () => {
                this.findClosestLocation(get(mapLocations), this.pos).then(
                    (locations) => {
                        mapLocations.set(
                            [...locations].sort(
                                (a, b) => a.distance - b.distance,
                            ),
                        )
                    },
                )
                this.centerMap(`${this.pos.lat},${this.pos.lng}`, 11)

                searchInput.value = ''

                resetInput.style.display = 'none'
            })
        }
    }

    async fetchCoordinates(address) {
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${window.mapOptions.api_key}`,
        ).catch((e) => {
            throw new Error(e)
        })

        const data = await response.json()

        return data
    }

    focusLocation(location) {
        this.centerMap(
            `${location.coordinates.lat},${location.coordinates.lng}`,
        )
    }
}

export default DistrictMap
